import { useCurrentGameProfileQueryIfGameAvailable } from "@entities/user/api/useCurrentGameProfileQueryIfGameAvailable.ts";
import { queries } from "@fxid/openapi-client";
import { authTokenExists } from "@fxid/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import log from "loglevel";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import OpenApiClient from "../../../../packages/fxid-openapi-client/src/OpenApiClient.ts";

/**
 * @fileoverview Хук для двунаправленной синхронизации языка между
 * состоянием i18next и профилем пользователя на сервере.
 */
export const useI18nSync = () => {
	const { i18n } = useTranslation();
	const queryClient = useQueryClient();

	// Получаем статус аутентификации
	const isAuthenticated = authTokenExists();

	log.debug("isAuthenticated", isAuthenticated);
	// 1. Получаем профиль пользователя с помощью React Query.
	// Запрос будет активен только если пользователь авторизован.
	const { data: profile } = useCurrentGameProfileQueryIfGameAvailable();

	// 2. Создаем мутацию для обновления профиля.
	const { mutate: updateProfileLanguage } = useMutation({
		mutationFn: (newLang: string) =>
			// Вызываем API-метод напрямую
			OpenApiClient.Profile.fxIdWebFeaturesProfileUpdateProfileSettingsEndpoint({
				Language: newLang,
				DUMMY: false
			}),
		onSuccess: () => {
			log.debug("User language successfully updated on server");
			// После успешного обновления инвалидируем кэш профиля,
			// чтобы получить свежие данные. Используем ключ из фабрики.
			void queryClient.invalidateQueries({ queryKey: queries.profile.get._def });
		},
		onError: (error) => {
			log.error("Failed to update user language on server", error);
		}
	});

	// 3. Эффект для синхронизации языка из профиля в i18next.
	// Срабатывает при загрузке профиля или когда он меняется.
	useEffect(() => {
		const languageFromProfile = profile?.Locale;
		if (languageFromProfile && languageFromProfile !== i18n.language) {
			log.debug(`Syncing language from profile to i18next: ${languageFromProfile}`);
			void i18n.changeLanguage(languageFromProfile);
		}
	}, [profile?.Locale, i18n]);

	// 4. Эффект для синхронизации языка из i18next в профиль.
	// Используем `useEffect` для подписки/отписки на событие i18next.
	useEffect(() => {
		const handleLanguageChange = (newLang: string) => {
			// Обновляем язык на сервере, только если:
			// - Пользователь авторизован
			// - Язык в профиле существует
			// - Новый язык отличается от языка в профиле
			if (isAuthenticated && profile?.Locale && newLang !== profile.Locale) {
				log.debug(`Syncing language from i18next to profile: ${newLang}`);
				updateProfileLanguage(newLang);
			}
		};

		i18n.on("languageChanged", handleLanguageChange);

		return () => {
			i18n.off("languageChanged", handleLanguageChange);
		};
	}, [i18n, isAuthenticated, profile?.Locale, updateProfileLanguage]);
};
